
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { format } from "date-fns";

const Billing = () => {
  const { subscriptionPlans, userSubscriptions, monthlyRevenue, loading, refetch } = useSubscriptions();

  if (loading) {
    return <div className="p-8 text-center">Loading billing data...</div>;
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'trialing': return 'secondary';
      case 'past_due': return 'destructive';
      case 'canceled': return 'outline';
      case 'expired': return 'destructive';
      default: return 'outline';
    }
  };

  const getDisplayDates = (subscription: any) => {
    if (subscription.status === 'trialing') {
      return {
        startDate: subscription.trial_start_date,
        endDate: subscription.trial_end_date
      };
    } else {
      return {
        startDate: subscription.subscription_start_date,
        endDate: subscription.subscription_end_date
      };
    }
  };

  const getUserDisplayName = (subscription: any) => {
    const firstName = subscription.user_first_name || '';
    const lastName = subscription.user_last_name || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    return subscription.user_id; // Fallback to user ID if no name available
  };

  const getActivePaidSubscriptionsCount = () => {
    return userSubscriptions.filter(sub => {
      return sub.status === 'active' && sub.plan_price > 0;
    }).length;
  };

  const formatPrice = (subscription: any) => {
    // Handle trial subscriptions
    if (subscription.status === 'trialing') {
      return <span className="text-green-600">Free Trial</span>;
    }
    
    // Handle free plans (price = 0)
    if (subscription.plan_price === 0 || subscription.plan_price === null) {
      return <span className="text-green-600">Free</span>;
    }
    
    // Handle paid plans
    return `$${subscription.plan_price.toFixed(2)}/${subscription.plan_billing_cycle || 'month'}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Billing & Subscriptions</h1>
        <p className="text-muted-foreground">
          Manage your subscription plans and view billing information
        </p>
      </div>

      {/* Revenue Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Summary</CardTitle>
          <CardDescription>Monthly recurring revenue from active paid subscriptions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">${monthlyRevenue.toFixed(2)}</div>
          <p className="text-xs text-muted-foreground">
            From {getActivePaidSubscriptionsCount()} active paid subscriptions
          </p>
          <Button onClick={refetch} variant="outline" className="mt-2">
            Refresh Data
          </Button>
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Available Subscription Plans</CardTitle>
          <CardDescription>Current pricing and features for all plans</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {subscriptionPlans.map((plan) => (
              <div key={plan.id} className="border rounded-lg p-4">
                <div className="space-y-2">
                  <h3 className="font-semibold">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                  <div className="text-2xl font-bold">
                    ${plan.price.toFixed(2)}
                    <span className="text-sm text-muted-foreground">/{plan.billing_cycle}</span>
                  </div>
                  <div className="space-y-1">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="text-sm text-muted-foreground">
                        • {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Customer Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Subscriptions</CardTitle>
          <CardDescription>All customer subscriptions and their current status</CardDescription>
        </CardHeader>
        <CardContent>
          {userSubscriptions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No subscriptions found
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userSubscriptions.map((subscription) => {
                  const dates = getDisplayDates(subscription);
                  const customerName = getUserDisplayName(subscription);
                  
                  return (
                    <TableRow key={subscription.id}>
                      <TableCell className="font-medium">
                        {customerName}
                      </TableCell>
                      <TableCell>
                        {subscription.plan_name || 'Unknown Plan'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(subscription.status)}>
                          {subscription.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {dates.startDate ? 
                          format(new Date(dates.startDate), 'MMM dd, yyyy') : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {dates.endDate ? 
                          format(new Date(dates.endDate), 'MMM dd, yyyy') : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {formatPrice(subscription)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Subscription Analytics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userSubscriptions.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {userSubscriptions.filter(sub => sub.status === 'active' && sub.plan_price > 0).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Trial</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {userSubscriptions.filter(sub => sub.status === 'trialing').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired/Canceled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {userSubscriptions.filter(sub => ['expired', 'canceled'].includes(sub.status)).length}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Billing;
